import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Calendar, Plus, Check, X, Ban, User, Zap, Repeat, Save, Video, Phone } from "lucide-react";

const STATUS_LABEL = { available: "Open", booked: "Booked", completed: "Completed", no_show: "No show", cancelled: "Cancelled", blocked: "Blocked" };
const STATUS_TONE = {
  available: "bg-primary/15 text-primary",
  booked: "bg-accent/15 text-accent",
  completed: "bg-secondary text-foreground",
  no_show: "bg-destructive/15 text-destructive",
  cancelled: "bg-secondary text-muted-foreground",
  blocked: "bg-secondary text-muted-foreground",
};
const SERVICE_LABEL = {
  simple: "Simple return",
  family: "EIC & CTC return",
  self_employed: "Self-employed",
  amendment: "Amendment",
  consult: "Consult",
};

// US federal holidays (observed) covering 2026 & 2027 tax seasons
const HOLIDAYS = [
  { ymd: "2026-01-01", label: "New Year's Day" },
  { ymd: "2026-01-19", label: "MLK Day" },
  { ymd: "2026-02-16", label: "Presidents' Day" },
  { ymd: "2026-05-25", label: "Memorial Day" },
  { ymd: "2026-06-19", label: "Juneteenth" },
  { ymd: "2026-07-03", label: "Independence Day (obs.)" },
  { ymd: "2026-09-07", label: "Labor Day" },
  { ymd: "2026-10-12", label: "Columbus Day" },
  { ymd: "2026-11-11", label: "Veterans Day" },
  { ymd: "2026-11-26", label: "Thanksgiving" },
  { ymd: "2026-12-25", label: "Christmas" },
  { ymd: "2027-01-01", label: "New Year's Day '27" },
  { ymd: "2027-01-18", label: "MLK Day '27" },
  { ymd: "2027-02-15", label: "Presidents' Day '27" },
  { ymd: "2027-04-15", label: "Tax Day '27 (whew)" },
];

const roundedIsoNow = () => {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 24);
  return d.toISOString().slice(0, 16);
};

export default function AdminAppointments() {
  const [rows, setRows] = useState([]);
  const [newStart, setNewStart] = useState(roundedIsoNow());
  const [newDuration, setNewDuration] = useState(30);

  // Bulk generator (single day OR range with weekday filter)
  const todayYMD = () => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  };
  const [genDate, setGenDate] = useState(todayYMD);
  const [genEndDate, setGenEndDate] = useState("");
  const [genWeekdays, setGenWeekdays] = useState([0,1,2,3,4,5,6]); // Sun=0 ... Sat=6
  const [genClientType, setGenClientType] = useState("returning");
  const [genStart, setGenStart] = useState(9);
  const [genEnd, setGenEnd] = useState(18);
  const [genBuffer, setGenBuffer] = useState(0);
  const [genning, setGenning] = useState(false);

  // Availability template
  const [tmpl, setTmpl] = useState(null);
  const [tmplSaving, setTmplSaving] = useState(false);
  const [tmplRangeStart, setTmplRangeStart] = useState(todayYMD);
  const [tmplRangeEnd, setTmplRangeEnd] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    api.get("/admin/availability-template").then((r) => setTmpl(r.data)).catch(() => {});
  }, []);

  const updateTmpl = (wd, patch) => setTmpl((t) => t ? ({
    ...t, weekly: { ...t.weekly, [wd]: { ...t.weekly[wd], ...patch } }
  }) : t);
  const toggleOffDate = (ymd) => setTmpl((t) => {
    if (!t) return t;
    const cur = new Set(t.off_dates || []);
    if (cur.has(ymd)) cur.delete(ymd);
    else cur.add(ymd);
    return { ...t, off_dates: Array.from(cur).sort() };
  });
  const saveTmpl = async () => {
    if (!tmpl) return;
    setTmplSaving(true);
    try {
      await api.put("/admin/availability-template", { weekly: tmpl.weekly, off_dates: tmpl.off_dates || [] });
      toast.success("My usual week saved.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setTmplSaving(false); }
  };
  const applyTmpl = async () => {
    if (!tmplRangeStart || !tmplRangeEnd) { toast.error("Pick start and end dates"); return; }
    setApplying(true);
    try {
      const r = await api.post("/admin/availability-template/apply", {
        date: tmplRangeStart, end_date: tmplRangeEnd,
      });
      toast.success(`Applied — ${r.data.created} slot(s) across ${r.data.days_touched} day(s).`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setApplying(false); }
  };

  const toggleWeekday = (n) => setGenWeekdays((w) => w.includes(n) ? w.filter(x => x !== n) : [...w, n].sort());
  const setSaturdaysOnly = () => { setGenWeekdays([6]); setGenClientType("new"); setGenStart(10); setGenEnd(16); setGenBuffer(10); };
  const setSunToFri = () => { setGenWeekdays([0,1,2,3,4,5]); setGenStart(9); setGenEnd(18); };
  const setAllDays = () => setGenWeekdays([0,1,2,3,4,5,6]);

  const load = () => api.get("/admin/appointments").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generateDay = async () => {
    if (genWeekdays.length === 0) { toast.error("Pick at least one weekday."); return; }
    setGenning(true);
    try {
      const r = await api.post("/admin/appointments/generate-day", {
        date: genDate,
        end_date: genEndDate || "",
        weekdays: genWeekdays.length === 7 ? [] : genWeekdays, // empty means all
        start_hour: Number(genStart),
        end_hour: Number(genEnd),
        duration_min: genClientType === "new" ? 60 : 30,
        buffer_min: Number(genBuffer) || 0,
        skip_lunch: true,
      });
      const rangeLabel = genEndDate ? `${genDate} → ${genEndDate}` : genDate;
      toast.success(`Created ${r.data.created} ${genClientType === "new" ? "1-hr" : "30-min"} slots across ${r.data.days_touched} day(s) (${rangeLabel}).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate");
    } finally { setGenning(false); }
  };

  // Group slots into Today / Upcoming / Past for at-a-glance daily use
  const groups = React.useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(startOfToday.getTime() + 24 * 3600 * 1000);
    const today = [], upcoming = [], past = [];
    for (const r of rows) {
      const t = new Date(r.start_at);
      if (t < startOfToday) past.push(r);
      else if (t < endOfToday) today.push(r);
      else upcoming.push(r);
    }
    // upcoming sorted ascending, past sorted descending
    past.sort((a, b) => new Date(b.start_at) - new Date(a.start_at));
    return { today, upcoming, past };
  }, [rows]);

  const create = async () => {
    if (!newStart) { toast.error("Pick a start time"); return; }
    try {
      // Convert local datetime-local to UTC ISO
      const iso = new Date(newStart).toISOString();
      await api.post("/admin/appointments/slots", { start_at: iso, duration_min: Number(newDuration), notes: "" });
      toast.success("Slot created");
      setNewStart(roundedIsoNow());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}?status=${status}`);
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this slot?")) return;
    try { await api.delete(`/admin/appointments/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 lg:p-12">
        <div className="label-caps mb-3 flex items-center gap-2"><Calendar size={14} /> Appointments</div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-8">Bookings & availability.</h1>

        {/* My usual week template */}
        {tmpl && (
          <Card className="p-4 mb-4" data-testid="admin-tmpl">
            <div className="label-caps text-[10px] mb-3 flex items-center gap-2"><Repeat size={12} className="text-primary" /> My usual week · save once, apply anywhere</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[720px]">
                <thead>
                  <tr className="text-muted-foreground">
                    <th className="text-left py-1.5 pr-2 label-caps text-[10px] font-normal">Day</th>
                    <th className="text-left py-1.5 pr-2 label-caps text-[10px] font-normal">Off</th>
                    <th className="text-left py-1.5 pr-2 label-caps text-[10px] font-normal">Start (24h)</th>
                    <th className="text-left py-1.5 pr-2 label-caps text-[10px] font-normal">End (24h)</th>
                    <th className="text-left py-1.5 pr-2 label-caps text-[10px] font-normal">Duration</th>
                    <th className="text-left py-1.5 pr-2 label-caps text-[10px] font-normal">Buffer</th>
                  </tr>
                </thead>
                <tbody>
                  {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((label, wd) => {
                    const d = tmpl.weekly[String(wd)] || {};
                    return (
                      <tr key={wd} className={`border-t border-border ${d.off ? "opacity-60" : ""}`} data-testid={`admin-tmpl-row-${wd}`}>
                        <td className="py-1.5 pr-2 font-medium">{label}</td>
                        <td className="py-1.5 pr-2">
                          <input
                            type="checkbox"
                            checked={!!d.off}
                            onChange={(e) => updateTmpl(String(wd), { off: e.target.checked })}
                            data-testid={`admin-tmpl-off-${wd}`}
                            className="h-4 w-4 accent-primary"
                          />
                        </td>
                        <td className="py-1.5 pr-2"><Input type="number" min="0" max="23" value={d.start_hour ?? 9} disabled={d.off} className="h-8 w-16" onChange={(e) => updateTmpl(String(wd), { start_hour: Number(e.target.value) })} /></td>
                        <td className="py-1.5 pr-2"><Input type="number" min="1" max="23" value={d.end_hour ?? 18} disabled={d.off} className="h-8 w-16" onChange={(e) => updateTmpl(String(wd), { end_hour: Number(e.target.value) })} /></td>
                        <td className="py-1.5 pr-2">
                          <select
                            value={d.duration_min ?? 30}
                            disabled={d.off}
                            onChange={(e) => updateTmpl(String(wd), { duration_min: Number(e.target.value) })}
                            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                            data-testid={`admin-tmpl-dur-${wd}`}
                          >
                            <option value={30}>30 min (returning)</option>
                            <option value={60}>60 min (new)</option>
                          </select>
                        </td>
                        <td className="py-1.5 pr-2"><Input type="number" min="0" max="30" value={d.buffer_min ?? 0} disabled={d.off} className="h-8 w-16" onChange={(e) => updateTmpl(String(wd), { buffer_min: Number(e.target.value) })} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-3 pt-3 border-t border-border">
              <div className="flex items-center justify-between mb-2">
                <div className="label-caps text-[10px]">Days off · federal holidays</div>
                {tmpl.off_dates?.length > 0 && (
                  <span className="text-[10px] text-muted-foreground">{tmpl.off_dates.length} selected</span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="admin-tmpl-holidays">
                {HOLIDAYS.map((h) => {
                  const on = (tmpl.off_dates || []).includes(h.ymd);
                  return (
                    <button
                      key={h.ymd}
                      type="button"
                      onClick={() => toggleOffDate(h.ymd)}
                      data-testid={`admin-tmpl-hol-${h.ymd}`}
                      className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
                        on ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"
                      }`}
                      title={h.ymd}
                    >
                      {h.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                Click a holiday to block it. When you "Apply to range" below, those days are skipped entirely.
              </p>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3 pt-3 border-t border-border">
              <Button size="sm" onClick={saveTmpl} disabled={tmplSaving} data-testid="admin-tmpl-save"><Save size={12} className="mr-1" /> {tmplSaving ? "Saving…" : "Save my usual week"}</Button>
              <div className="flex items-center gap-2 ml-auto">
                <Label className="label-caps text-[10px]">Apply from</Label>
                <Input type="date" value={tmplRangeStart} onChange={(e) => setTmplRangeStart(e.target.value)} className="h-8 w-40" data-testid="admin-tmpl-range-start" />
                <Label className="label-caps text-[10px]">to</Label>
                <Input type="date" value={tmplRangeEnd} onChange={(e) => setTmplRangeEnd(e.target.value)} className="h-8 w-40" data-testid="admin-tmpl-range-end" />
                <Button size="sm" variant="outline" onClick={applyTmpl} disabled={applying} data-testid="admin-tmpl-apply">
                  <Zap size={12} className="mr-1" /> {applying ? "Applying…" : "Apply to range"}
                </Button>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              This is your default schedule. Set Saturday to "off" if you don't take Saturday appointments any given season. Applying skips 12–1 PM lunch and skips slots that already exist.
            </p>
          </Card>
        )}

        <Card className="p-4 mb-4" data-testid="admin-gen-day">
          <div className="label-caps text-[10px] mb-3 flex items-center gap-2"><Zap size={12} className="text-primary" /> Generate slots · single day or range</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div className="space-y-1">
              <Label className="label-caps text-[10px]">Start date</Label>
              <Input type="date" value={genDate} onChange={e => setGenDate(e.target.value)} data-testid="admin-gen-date" />
            </div>
            <div className="space-y-1">
              <Label className="label-caps text-[10px]">End date <span className="normal-case tracking-normal text-muted-foreground">(optional — for range)</span></Label>
              <Input type="date" value={genEndDate} onChange={e => setGenEndDate(e.target.value)} data-testid="admin-gen-end-date" />
            </div>
            <div className="space-y-1">
              <Label className="label-caps text-[10px]">Client type</Label>
              <select
                value={genClientType}
                onChange={e => setGenClientType(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="admin-gen-client-type"
              >
                <option value="returning">Returning (30 min)</option>
                <option value="new">New client (1 hr)</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="label-caps text-[10px]">Buffer between slots (min)</Label>
              <Input type="number" min="0" max="30" value={genBuffer} onChange={e => setGenBuffer(e.target.value)} data-testid="admin-gen-buffer" />
            </div>
          </div>

          {genEndDate && (
            <div className="mb-3">
              <Label className="label-caps text-[10px] mb-2 block">Days to include</Label>
              <div className="flex flex-wrap gap-1.5">
                {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((label, i) => {
                  const on = genWeekdays.includes(i);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggleWeekday(i)}
                      data-testid={`admin-gen-wd-${i}`}
                      className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                        on ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <button type="button" onClick={setSaturdaysOnly} className="text-[11px] underline text-muted-foreground hover:text-foreground" data-testid="admin-gen-preset-sat">Saturdays only</button>
                <span className="text-[11px] text-muted-foreground">·</span>
                <button type="button" onClick={setSunToFri} className="text-[11px] underline text-muted-foreground hover:text-foreground" data-testid="admin-gen-preset-sunfri">Sun–Fri (business hours)</button>
                <span className="text-[11px] text-muted-foreground">·</span>
                <button type="button" onClick={setAllDays} className="text-[11px] underline text-muted-foreground hover:text-foreground" data-testid="admin-gen-preset-all">Every day</button>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-3 gap-3 items-end">
            <div className="space-y-1">
              <Label className="label-caps text-[10px]">Start hour (24h)</Label>
              <Input type="number" min="0" max="23" value={genStart} onChange={e => setGenStart(e.target.value)} data-testid="admin-gen-start" />
            </div>
            <div className="space-y-1">
              <Label className="label-caps text-[10px]">End hour (24h)</Label>
              <Input type="number" min="1" max="23" value={genEnd} onChange={e => setGenEnd(e.target.value)} data-testid="admin-gen-end" />
            </div>
            <Button onClick={generateDay} disabled={genning} data-testid="admin-gen-btn">
              <Zap size={12} className="mr-1" /> {genning ? "Generating…" : (genEndDate ? "Generate range" : "Generate day")}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            Skips 12–1 PM for lunch. Skips slots that already exist. Times are Eastern (Winston-Salem). {genEndDate ? "Use the day chips above to seed a whole tax-season worth of Saturdays or every business day in one click." : "Add an End date above to generate a whole range with weekday filtering."}
          </p>
        </Card>

        <Card className="p-4 mb-8" data-testid="admin-new-slot">
          <div className="label-caps text-[10px] mb-3">Open a new slot</div>
          <div className="grid sm:grid-cols-[1fr_120px_140px] gap-3 items-end">
            <div className="space-y-1">
              <Label className="label-caps text-[10px]">Start (your local time)</Label>
              <Input type="datetime-local" value={newStart} onChange={e => setNewStart(e.target.value)} data-testid="admin-slot-start" />
            </div>
            <div className="space-y-1">
              <Label className="label-caps text-[10px]">Duration (min)</Label>
              <Input type="number" min="15" max="180" step="15" value={newDuration} onChange={e => setNewDuration(e.target.value)} data-testid="admin-slot-duration" />
            </div>
            <Button onClick={create} data-testid="admin-slot-create-btn"><Plus size={12} className="mr-1" /> Add slot</Button>
          </div>
        </Card>

        {rows.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">No slots yet. Add one above and share <code className="text-xs">/book</code>.</Card>
        ) : (
          <div className="space-y-8" data-testid="admin-slots-list">
            <SlotGroup title="Today" testid="slots-today" rows={groups.today} emptyText="No appointments on the books for today." onStatus={updateStatus} onDelete={del} />
            <SlotGroup title="Upcoming" testid="slots-upcoming" rows={groups.upcoming} emptyText="Nothing coming up — add slots above so clients can book." onStatus={updateStatus} onDelete={del} />
            {groups.past.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground label-caps text-[10px]">Past ({groups.past.length})</summary>
                <div className="mt-3">
                  <SlotGroup rows={groups.past} testid="slots-past" onStatus={updateStatus} onDelete={del} bare />
                </div>
              </details>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function SlotGroup({ title, testid, rows, emptyText, onStatus, onDelete, bare }) {
  return (
    <section data-testid={testid}>
      {title && (
        <div className="label-caps text-[10px] mb-3 flex items-center gap-2">
          {title} <span className="text-muted-foreground normal-case tracking-normal">· {rows.length}</span>
        </div>
      )}
      {rows.length === 0 ? (
        <div className={`text-sm text-muted-foreground ${bare ? "" : "py-3"}`}>{emptyText}</div>
      ) : (
        <div className="space-y-2">
          {rows.map(r => <SlotRow key={r.id} r={r} onStatus={onStatus} onDelete={onDelete} />)}
        </div>
      )}
    </section>
  );
}

function SlotRow({ r, onStatus, onDelete }) {
  return (
    <Card className={`p-4 flex flex-wrap items-center gap-3 ${r.missing_contact ? "border-destructive/40 bg-destructive/5" : ""}`} data-testid={`admin-slot-${r.id}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="text-sm font-medium">{new Date(r.start_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
        <div className="text-xs text-muted-foreground">{r.duration_min} min</div>
      </div>
      {r.booking ? (
        <div className="flex-1 min-w-[220px] text-xs">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <User size={11} /><strong>{r.booking.first_name} {r.booking.last_name}</strong>
            {r.missing_contact && (
              <span className="text-[9px] label-caps px-1.5 py-0.5 rounded bg-destructive/15 text-destructive" data-testid={`slot-missing-contact-${r.id}`}>
                No email — reminder blocked
              </span>
            )}
            {r.booking.client_type && (
              <span
                className={`text-[9px] label-caps px-1.5 py-0.5 rounded ${
                  r.booking.client_type === "new"
                    ? "bg-accent/20 text-accent"
                    : "bg-primary/15 text-primary"
                }`}
                data-testid={`slot-client-type-${r.id}`}
              >
                {r.booking.client_type === "new" ? "New" : "Returning"}
              </span>
            )}
            {r.booking.meeting_type === "video" ? (
              <span className="text-[9px] label-caps px-1.5 py-0.5 rounded bg-primary/15 text-primary flex items-center gap-1"><Video size={9} /> Video</span>
            ) : (
              <span className="text-[9px] label-caps px-1.5 py-0.5 rounded bg-secondary text-muted-foreground flex items-center gap-1"><Phone size={9} /> Phone</span>
            )}
          </div>
          <div className="text-muted-foreground">{[r.booking.email, r.booking.phone].filter(Boolean).join(" · ")}</div>
          <div className="mt-0.5">{SERVICE_LABEL[r.booking.service_type] || r.booking.service_type}{r.booking.notes && ` — ${r.booking.notes}`}</div>
          {r.booking.meeting_type === "video" && r.booking.video_link && (
            <a
              href={r.booking.video_link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary underline"
              data-testid={`slot-video-link-${r.id}`}
            >
              <Video size={10} /> Join video call
            </a>
          )}
        </div>
      ) : (
        <div className="flex-1 min-w-[220px] text-xs text-muted-foreground">—</div>
      )}
      <span className={`text-[10px] label-caps px-2 py-1 rounded shrink-0 ${STATUS_TONE[r.status] || ""}`}>
        {STATUS_LABEL[r.status] || r.status}
      </span>
      <div className="flex gap-1 shrink-0">
        {r.status === "booked" && (
          <>
            <Button size="sm" variant="outline" onClick={() => onStatus(r.id, "completed")} data-testid={`slot-complete-${r.id}`}><Check size={12} className="mr-1" /> Done</Button>
            <Button size="sm" variant="outline" onClick={() => onStatus(r.id, "no_show")} data-testid={`slot-noshow-${r.id}`}><X size={12} className="mr-1" /> No show</Button>
            <Button size="sm" variant="outline" onClick={() => onStatus(r.id, "cancelled")} data-testid={`slot-cancel-${r.id}`}>Cancel</Button>
          </>
        )}
        {r.status === "available" && (
          <Button size="sm" variant="outline" onClick={() => onStatus(r.id, "blocked")} data-testid={`slot-block-${r.id}`}><Ban size={12} className="mr-1" /> Block</Button>
        )}
        {(r.status === "blocked" || r.status === "cancelled" || r.status === "no_show") && (
          <Button size="sm" variant="outline" onClick={() => onStatus(r.id, "available")} data-testid={`slot-reopen-${r.id}`}>Reopen</Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => onDelete(r.id)} data-testid={`slot-delete-${r.id}`}><X size={12} /></Button>
      </div>
    </Card>
  );
}
